import { useEffect, useRef } from 'react';

import { useTranslation } from '@splitfiesta/i18n';

import { useTypedApi } from '@/hooks/useTypedApi';

import { useAuthStore } from '@/stores/authStore';

const LANGUAGE_SESSION_KEY = 'user_language_applied';

export function ApplyUserLanguage() {
  const user = useAuthStore(state => state.user);
  const { i18n } = useTranslation();
  const api = useTypedApi();
  const hasAppliedRef = useRef(false);

  useEffect(() => {
    if (!user) {
      // Reset when user logs out
      hasAppliedRef.current = false;
      sessionStorage.removeItem(LANGUAGE_SESSION_KEY);
      return;
    }

    // Check if we've already applied language in this session
    const hasAppliedInSession = sessionStorage.getItem(LANGUAGE_SESSION_KEY);
    if (hasAppliedInSession === user.id || hasAppliedRef.current) {
      return;
    }

    // Mark as applied immediately to prevent concurrent calls
    hasAppliedRef.current = true;
    sessionStorage.setItem(LANGUAGE_SESSION_KEY, user.id);

    // Apply user's language preference
    const applyLanguage = async () => {
      try {
        const response = await api.user.getSettings();
        const settings = response.data as { language?: string };

        if (settings?.language && settings.language !== i18n.language) {
          await i18n.changeLanguage(settings.language);
        }
      } catch {
        // Reset on error so it can retry later
        hasAppliedRef.current = false;
        sessionStorage.removeItem(LANGUAGE_SESSION_KEY);
      }
    };

    applyLanguage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]); // Only depend on user ID, not the entire user object or api/i18n

  return null;
}
